import { Group } from '@any-ui-react/core';
import { Radio } from '@mantine/core';
import classNames from 'classnames';
import filter from 'lodash-es/filter';
import flow from 'lodash-es/flow';
import groupBy from 'lodash-es/groupBy';
import map from 'lodash-es/map';
import reverse from 'lodash-es/reverse';
import sortBy from 'lodash-es/sortBy';
import toPairs from 'lodash-es/toPairs';
import { useMemo, useState } from 'react';

import { PerformanceReviewStageBadge } from '@/common/components/shared/badge';
import { HoverCardWithIcon } from '@/common/components/shared/hover-card';
import { PerformanceReviewLink } from '@/common/components/shared/link';
import { MaterialIcons } from '@/common/components/shared/material-icons';
import { NoResult } from '@/common/components/shared/no-result';
import { Table, type TableLayout } from '@/common/components/shared/table';
import { EvaluatorType } from '@/common/graphql';
import { comparePerformanceReviewStage } from '@/common/types';
import { PerformanceReviewUtils } from '@/common/utils';
import type { DashboardPerformanceReview } from '../../types';

const currentPeriod = PerformanceReviewUtils.getCurrentPerformanceReviewPeriod();

interface Props {
  data: Array<DashboardPerformanceReview>;
  loading: boolean;
  showPeriodFilter: boolean;
  withName: boolean;
}

export const PerformanceReviewTable = ({ data, loading, showPeriodFilter, withName }: Props) => {
  const [filterYearQuarter, setFilterYearQuarter] = useState(
    `${currentPeriod.year} ${PerformanceReviewUtils.quarterToString(currentPeriod.quarter)}`,
  );

  const layout: Array<TableLayout<DashboardPerformanceReview>> = useMemo(
    () => [
      ...(withName
        ? ([
            {
              headerRender: () => 'Name',
              accessor: 'name',
              truncated: false,
              cellRender: ({ id, employee, period, validation }) => (
                <div className="flex flex-nowrap items-center gap-x-1">
                  <PerformanceReviewLink
                    className={classNames('text-nowrap', { 'font-bold': !showPeriodFilter && period.isCurrent })}
                    performanceReviewId={id}
                  >
                    {employee.generalInformation.preferredFullName}
                  </PerformanceReviewLink>
                  <ValidationAlert mode={EvaluatorType.Reviewer} validation={validation} />
                </div>
              ),
            },
          ] as Array<TableLayout<DashboardPerformanceReview>>)
        : []),
      ...(!showPeriodFilter
        ? ([
            {
              headerRender: () => 'Year/Quarter',
              accessor: 'period',
              thClassName: '!max-w-[170px]',
              tdClassName: '!max-w-[170px] tabular-nums',
              truncated: false,
              cellRender: ({ period, validation }) => (
                <div className="flex flex-nowrap items-center gap-x-1">
                  <div
                    className={classNames({
                      'font-bold': !showPeriodFilter && period.isCurrent,
                      'text-gray-7': !showPeriodFilter && !period.isCurrent,
                    })}
                  >
                    {period.year}
                    &nbsp;
                    {PerformanceReviewUtils.quarterToString(period.quarter)}
                  </div>
                  <ValidationAlert mode={EvaluatorType.Self} validation={validation} />
                </div>
              ),
            },
          ] as Array<TableLayout<DashboardPerformanceReview>>)
        : []),
      {
        headerRender: () => 'Stage',
        accessor: 'stage',
        thClassName: '!max-w-[220px]',
        tdClassName: '!max-w-[220px]',
        cellRender: ({ period, setting, companyStage }) => (
          <div className="flex flex-nowrap items-center gap-x-2">
            <PerformanceReviewStageBadge stage={setting.stage} version={period.version} />
            {companyStage && comparePerformanceReviewStage(companyStage, setting.stage) === -1 && (
              <HoverCardWithIcon materialIconProps={{ iconName: 'warning', fill: true, className: 'text-warning' }}>
                Company stage:&nbsp;
                <PerformanceReviewStageBadge stage={companyStage} version={period.version} />
              </HoverCardWithIcon>
            )}
          </div>
        ),
      } as TableLayout<DashboardPerformanceReview>,
      ...(withName
        ? []
        : ([
            {
              headerRender: () => null,
              accessor: 'link',
              tdClassName: '!max-w-[inherit]',
              cellRender: ({ id, period }) => (
                <PerformanceReviewLink
                  className={classNames('text-nowrap', { 'font-bold': !showPeriodFilter && period.isCurrent })}
                  performanceReviewId={id}
                >
                  Go to Review
                </PerformanceReviewLink>
              ),
            },
          ] as Array<TableLayout<DashboardPerformanceReview>>)),
    ],
    [showPeriodFilter, withName],
  );

  const allPeriods: Array<string> = useMemo(
    () =>
      flow([
        (data: Array<DashboardPerformanceReview>) =>
          groupBy(data, it => PerformanceReviewUtils.periodToString(it.period)),
        (data: Record<string, Array<DashboardPerformanceReview>>) => toPairs(data),
        (data: [string, Array<DashboardPerformanceReview>][]) => map(data, ([period, items]) => [period, items.length]),
        (data: Array<[string, number]>) => filter(data, ([_, size]) => size > 0),
        (data: Array<[string, number]>) => sortBy(data, it => it[0]),
        (data: Array<[string, number]>) => reverse(data),
        (data: Array<[string, number]>) => map(data, ([period, _]) => period),
      ])(data),
    [data],
  );

  const filteredData = useMemo(
    () =>
      data.filter(it => {
        if (!showPeriodFilter || allPeriods.length <= 1) return true;
        const selected = PerformanceReviewUtils.stringToPeriod(filterYearQuarter);
        return selected.year === it.period.year && selected.quarter === it.period.quarter;
      }),
    [data, showPeriodFilter, allPeriods, filterYearQuarter],
  );

  return (
    <>
      {showPeriodFilter && allPeriods.length > 1 && (
        <Radio.Group className="mb-2 ml-3.5" value={filterYearQuarter} onChange={setFilterYearQuarter}>
          <Group className="gap-x-6 gap-y-2">
            {allPeriods.map(period => (
              <Radio
                key={period}
                classNames={{
                  label: classNames('tabular-nums', {
                    'font-semibold': period === PerformanceReviewUtils.periodToString(currentPeriod),
                  }),
                }}
                label={period}
                value={period}
              />
            ))}
          </Group>
        </Radio.Group>
      )}
      <Table<DashboardPerformanceReview>
        allowFloatingHeader={false}
        data={filteredData}
        headerClassName="h-auto [&>th]:py-2"
        layout={layout}
        loading={loading}
        notFoundTemplate={
          <NoResult title="No data available" titleClassName="text-sm text-gray-9" wrapperClassName="py-10" />
        }
        numOfStickyCols={0}
        page={1}
        scrollContainerClassName="max-h-[250px]"
        skeletonCount={3}
        wrapperClassName="rounded border-x border-b overflow-auto"
      />
    </>
  );
};

const ValidationAlert = ({
  mode,
  validation,
}: {
  mode: EvaluatorType;
  validation: DashboardPerformanceReview['validation'];
}) => {
  const allErrorsEvaluatorTypes = useMemo(
    () => validation.flatMap(it => it.errors).map(it => it.evaluatorType),
    [validation],
  );

  if (validation.length === 0) {
    return <MaterialIcons iconName="check_circle" fontSize="16px" className="text-success" />;
  }

  if (mode === EvaluatorType.Self) {
    if (allErrorsEvaluatorTypes.includes(EvaluatorType.Self)) {
      return (
        <HoverCardWithIcon materialIconProps={{ iconName: 'warning', fill: true, className: 'text-failure' }}>
          You have to fix validation error(s).
        </HoverCardWithIcon>
      );
    } else {
      return (
        <HoverCardWithIcon materialIconProps={{ iconName: 'warning', fill: true, className: 'text-warning' }}>
          You&apos;re okay, but your manager needs to fix validation error(s).
        </HoverCardWithIcon>
      );
    }
  } else {
    if (allErrorsEvaluatorTypes.includes(EvaluatorType.Reviewer)) {
      return (
        <HoverCardWithIcon materialIconProps={{ iconName: 'warning', fill: true, className: 'text-failure' }}>
          You have to fix validation error(s).
        </HoverCardWithIcon>
      );
    } else {
      return (
        <HoverCardWithIcon materialIconProps={{ iconName: 'warning', fill: true, className: 'text-warning' }}>
          The member needs to fix validation error(s).
        </HoverCardWithIcon>
      );
    }
  }
};
